import { useRef } from 'react';
import { DsInput } from '@/modules/internal-v2/ds';
import type {
  ContactPersonMeta,
  EmailBlock,
  EmailBlockData,
  EmailBlockType,
} from '@/api/email-builder/email-builder.types.ts';
import { SalesPersonPicker } from '@/modules/internal-v2/shared/sales-person-picker.component';
import { BlockCard } from './block-card.component';
import { AddBlockMenu } from './add-block-menu.component';
import { VariableInsertButton } from './variable-insert-button.component';

interface EditorPaneProps {
  blocks: EmailBlock[];
  subject: string;
  preheader: string;
  contactPerson: ContactPersonMeta;
  /** Resolved from `contactPerson.email` against the candidate list; `null` while not pre-filled or after a manual override. */
  selectedSalesPersonCode: number | null;
  onSubjectChange: (value: string) => void;
  onPreheaderChange: (value: string) => void;
  onContactPersonEnabledChange: (enabled: boolean) => void;
  onSelectSalesPerson: (salesPersonCode: number | null) => void;
  onAddBlock: (type: EmailBlockType) => void;
  onUpdateBlock: (id: string, data: EmailBlockData) => void;
  onRemoveBlock: (id: string) => void;
  onMoveBlock: (id: string, direction: -1 | 1) => void;
}

/** Left pane: email metadata + the ordered block list + add-block control. */
export function EditorPane({
  blocks,
  subject,
  preheader,
  contactPerson,
  selectedSalesPersonCode,
  onSubjectChange,
  onPreheaderChange,
  onContactPersonEnabledChange,
  onSelectSalesPerson,
  onAddBlock,
  onUpdateBlock,
  onRemoveBlock,
  onMoveBlock,
}: EditorPaneProps) {
  // Refs let `VariableInsertButton` splice tokens at the input's caret
  // position rather than always appending.
  const subjectRef = useRef<HTMLInputElement>(null);
  const preheaderRef = useRef<HTMLInputElement>(null);

  return (
    <div className="eb-editor">
      <div className="eb-editor-meta">
        <div className="eb-field">
          <span className="eb-field-label">Betreff</span>
          <div className="eb-field-row">
            <DsInput
              ref={subjectRef}
              value={subject}
              placeholder="E-Mail-Betreff"
              onChange={(e) => onSubjectChange(e.target.value)}
            />
            <VariableInsertButton
              targetRef={subjectRef}
              value={subject}
              onChange={onSubjectChange}
              ariaLabel="Variable in Betreff einfügen"
            />
          </div>
        </div>
        <div className="eb-field">
          <span className="eb-field-label">Preheader (Vorschautext)</span>
          <div className="eb-field-row">
            <DsInput
              ref={preheaderRef}
              value={preheader}
              placeholder="Kurztext, der in der Inbox-Vorschau erscheint"
              onChange={(e) => onPreheaderChange(e.target.value)}
            />
            <VariableInsertButton
              targetRef={preheaderRef}
              value={preheader}
              onChange={onPreheaderChange}
              ariaLabel="Variable in Preheader einfügen"
            />
          </div>
        </div>

        <div className="eb-cp">
          <label className="eb-cp-toggle">
            <input
              type="checkbox"
              checked={contactPerson.enabled}
              onChange={(e) => onContactPersonEnabledChange(e.target.checked)}
            />
            <span className="eb-field-label">Ansprechpartner anzeigen</span>
          </label>

          {contactPerson.enabled && (
            <div className="eb-cp-body">
              <SalesPersonPicker
                value={selectedSalesPersonCode}
                onChange={onSelectSalesPerson}
                placeholder="Verkäufer auswählen"
                emptyLabel="Keine Verkäufer verfügbar."
              />

              {contactPerson.name ? (
                <dl className="eb-cp-summary">
                  <div className="eb-cp-row">
                    <dt>Name</dt>
                    <dd>{contactPerson.name}</dd>
                  </div>
                  {contactPerson.email && (
                    <div className="eb-cp-row">
                      <dt>E-Mail</dt>
                      <dd>{contactPerson.email}</dd>
                    </div>
                  )}
                  {contactPerson.phone && (
                    <div className="eb-cp-row">
                      <dt>Mobil</dt>
                      <dd>{contactPerson.phone}</dd>
                    </div>
                  )}
                  {contactPerson.officePhone && (
                    <div className="eb-cp-row">
                      <dt>Festnetz</dt>
                      <dd>{contactPerson.officePhone}</dd>
                    </div>
                  )}
                </dl>
              ) : (
                <p className="eb-cp-empty">
                  Wählen Sie einen Verkäufer, um den Ansprechpartner-Block
                  oberhalb der E-Mail zu füllen.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="eb-blocks ds-scroll">
        {blocks.length === 0 && (
          <div className="eb-blocks-empty">
            Noch keine Blöcke. Fügen Sie unten den ersten Block hinzu.
          </div>
        )}

        {blocks.map((block, index) => (
          <BlockCard
            key={block.id}
            block={block}
            index={index}
            total={blocks.length}
            onUpdate={onUpdateBlock}
            onRemove={onRemoveBlock}
            onMove={onMoveBlock}
          />
        ))}

        <AddBlockMenu onAdd={onAddBlock} />
      </div>
    </div>
  );
}
